import { useState } from 'react';
import { Button, ThemeToggle, type ButtonVariant, type ButtonSize } from '@fluent/ui';
import './PilotBReview.css';

/**
 * Phase 1 Pilot B — interactive review page for the <Button> primitive.
 * Renders every state + every variant × size combination using the real
 * component, so Tornike can press real buttons on his phone.
 *
 * Survives until Storybook is wired up, then this file is deleted.
 */
export function PilotBReview() {
  const [clicks, setClicks] = useState(0);
  const [loading, setLoading] = useState(false);
  const variants: ButtonVariant[] = ['primary', 'secondary', 'danger', 'neutral', 'ghost'];
  const sizes: ButtonSize[] = ['sm', 'md', 'lg'];

  const fakeSubmit = () => {
    setLoading(true);
    window.setTimeout(() => setLoading(false), 1400);
  };

  return (
    <main className="pilot-b">
      <header className="pilot-b__hero">
        <p className="pilot-b__kicker">Phase 1 · Pilot B</p>
        <h1>Button primitive</h1>
        <p className="pilot-b__lede">
          Press the primary button. Feel the 2px drop. That&apos;s the whole review — if it feels dead on your phone, we
          iterate.
        </p>
        <div className="pilot-b__theme-row">
          <ThemeToggle />
        </div>
      </header>

      {/* Round-1 focus: primary md, all six states */}
      <section className="pilot-b__section">
        <h2>Primary · md · six states</h2>
        <p className="pilot-b__note">The brief focuses this pilot. Everything else below is secondary reference.</p>

        <div className="pilot-b__grid">
          <Frame label="Default">
            <Button onClick={() => setClicks((n) => n + 1)}>გააგრძელე</Button>
          </Frame>

          <Frame label="Hover (desktop)">
            <Button className="force-hover">გააგრძელე</Button>
          </Frame>

          <Frame label="Pressed">
            <Button className="force-active">გააგრძელე</Button>
          </Frame>

          <Frame label="Focused">
            <Button className="force-focus">გააგრძელე</Button>
          </Frame>

          <Frame label="Disabled">
            <Button disabled>გააგრძელე</Button>
          </Frame>

          <Frame label="Loading">
            <Button loading>გააგრძელე</Button>
          </Frame>
        </div>

        <div className="pilot-b__tapstrip">
          <p className="pilot-b__tap-label">Tap counter</p>
          <div className="pilot-b__tap-row">
            <Button fullWidth size="lg" onClick={() => setClicks((n) => n + 1)}>
              დააჭირე · {clicks}
            </Button>
          </div>
          <div className="pilot-b__tap-row">
            <Button fullWidth size="md" variant="secondary" onClick={fakeSubmit} loading={loading}>
              {loading ? 'იტვირთება…' : 'გაგზავნე'}
            </Button>
          </div>
        </div>
      </section>

      {/* Reference — all variants × all sizes */}
      <section className="pilot-b__section">
        <h2>All variants × sizes</h2>
        <p className="pilot-b__note">
          These exist in code so consumer features can reach for them. Focus review on primary — the others follow its
          rules.
        </p>

        {sizes.map((size) => (
          <div key={size} className="pilot-b__row-group">
            <span className="pilot-b__row-label">size &ldquo;{size}&rdquo;</span>
            <div className="pilot-b__row">
              {variants.map((variant) => (
                <Button key={variant} variant={variant} size={size}>
                  {labelFor(variant)}
                </Button>
              ))}
            </div>
          </div>
        ))}
      </section>

      {/* Composition sanity */}
      <section className="pilot-b__section">
        <h2>Real layouts</h2>
        <p className="pilot-b__note">Buttons in the two spots they&apos;ll live most: a card footer and a full-width sticky CTA.</p>

        <div className="pilot-b__card">
          <p className="pilot-b__card-q">აირჩიე სწორი თარგმანი</p>
          <p className="pilot-b__card-a">&ldquo;Hello, how are you?&rdquo;</p>
          <div className="pilot-b__card-footer">
            <Button variant="neutral" size="md">უკან</Button>
            <Button variant="primary" size="md">შემოწმება</Button>
          </div>
        </div>

        <div className="pilot-b__sticky-cta">
          <Button variant="primary" size="lg" fullWidth>
            დაიწყე გაკვეთილი
          </Button>
        </div>
      </section>

      <footer className="pilot-b__footer">
        <p>
          <b>Round 1 of 3+.</b> Tell Jake: press physics OK? Shadow depth right? Radius right? Color right? Georgian
          label weight right?
        </p>
      </footer>
    </main>
  );
}

function Frame({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="pilot-b__frame">
      <span className="pilot-b__frame-label">{label}</span>
      <div className="pilot-b__frame-body">{children}</div>
    </div>
  );
}

function labelFor(variant: ButtonVariant): string {
  switch (variant) {
    case 'primary':
      return 'გააგრძელე';
    case 'secondary':
      return 'მოუსმინე';
    case 'danger':
      return 'წაშალე';
    case 'neutral':
      return 'გაუქმება';
    case 'ghost':
      return 'გაიგე მეტი';
  }
}
